import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection 
} from '@/components/WorkbookPage';
import { Shield, Target } from 'lucide-react';

export const Rule5Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule5 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule5', { ...data, [field]: value });
  };
  
  return (
    <WorkbookPage pageId="rule-5" prevPage="section-2-divider" nextPage="rule-6">
      <RuleHeader ruleNumber="5" title="Build Your Buffer (Emergency Fund)" section="control" />
      
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        An emergency fund is money set aside specifically for unexpected expenses—a car repair, 
        medical bill, job loss, or broken appliance. It's your financial cushion that keeps 
        one bad day from turning into a disaster.
      </Paragraph>
      <Paragraph>
        <strong>Start small.</strong> Even $500 provides real protection. The ultimate goal is 
        3-6 months of expenses, but any buffer is better than none.
      </Paragraph>
      
      <SectionTitle>Why It Matters</SectionTitle>
      <Paragraph>
        Without an emergency fund, unexpected expenses go on credit cards or force you to skip 
        bills. This creates a cycle of debt that's hard to escape. A buffer breaks that cycle.
      </Paragraph>
      
      <div className="my-8 flex items-center justify-center gap-8">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-3">
            <Shield className="h-10 w-10 text-success" />
          </div>
          <p className="text-sm font-medium">First Goal</p>
          <p className="text-2xl font-bold text-success">$500</p>
          <p className="text-xs text-muted-foreground">Starter Buffer</p>
        </div>
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <Target className="h-10 w-10 text-primary" />
          </div>
          <p className="text-sm font-medium">Ultimate Goal</p>
          <p className="text-2xl font-bold text-primary">3-6 mo</p>
          <p className="text-xs text-muted-foreground">Full Buffer</p>
        </div>
      </div>
      
      <Callout type="tip" title="Where to Keep It">
        Keep your emergency fund in a separate savings account—not mixed with spending money. 
        Online banks often offer higher interest rates. The key is easy access but not TOO 
        easy (no debit card attached).
      </Callout>
      
      <ExampleBox title="Building $500 in 6 Months">
        <p>$500 ÷ 6 months = ~$84/month</p>
        <p>$84 ÷ 4 weeks = ~$21/week</p>
        <p className="mt-2">That's skipping one meal out per week, or $3/day.</p>
        <p className="text-sm text-muted-foreground mt-2">
          If that's too much, start with $10/week ($520/year). Progress matters more than perfection.
        </p>
      </ExampleBox>
      
      <StepList steps={[
        "Calculate your target: Start with $500, then work toward 3-6 months of expenses.",
        "Open a separate savings account (online banks are great for this).",
        "Set up automatic transfers from each paycheck.",
        "Treat it like a bill—non-negotiable.",
        "Only use it for true emergencies (not wants or predictable expenses).",
        "When you use it, make a plan to rebuild it."
      ]} />
      
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">📝 Your Emergency Fund Worksheet</h3>
        
        <div className="space-y-6">
          <WorksheetInput
            label="My current emergency fund balance:"
            placeholder="$___"
            value={data.currentEmergencyFund || ''}
            onChange={(v) => updateData('currentEmergencyFund', v)}
          />
          
          <WorksheetInput
            label="My first target (start with $500):"
            placeholder="$___"
            value={data.emergencyGoal || ''}
            onChange={(v) => updateData('emergencyGoal', v)}
          />
          
          <WorksheetInput
            label="My monthly expenses (for calculating 3-6 month goal later):"
            placeholder="$___"
            value={data.monthlyExpenses || ''}
            onChange={(v) => updateData('monthlyExpenses', v)}
          />
          
          <WorksheetInput
            label="How much I can save each week/month toward this goal:"
            placeholder="$___ per week/month"
            value={data.monthlyContribution || ''}
            onChange={(v) => updateData('monthlyContribution', v)}
          />
          
          <WorksheetInput
            label="Where I will keep this fund (bank name/account type):"
            placeholder="Online savings, credit union, etc."
            value={data.fundLocation || ''}
            onChange={(v) => updateData('fundLocation', v)}
          />
        </div>
      </div>
      
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem checked={data.checklist1 || false} onChange={(v) => updateData('checklist1', v)}>
          I've set a first target amount for my emergency fund
        </CheckboxItem>
        <CheckboxItem checked={data.checklist2 || false} onChange={(v) => updateData('checklist2', v)}>
          I know how much I can contribute weekly/monthly
        </CheckboxItem>
        <CheckboxItem checked={data.checklist3 || false} onChange={(v) => updateData('checklist3', v)}>
          I have a plan for where to keep this money
        </CheckboxItem>
      </div>
      
      <RecapBox items={[
        "Start with $500, then build to 3-6 months of expenses",
        "Keep it separate from spending money",
        "Automate contributions like a bill",
        "Only use for true emergencies"
      ]} />
      
      <AffirmationBox>
        I am building a safety net that protects my future. Every dollar saved is a step toward security.
      </AffirmationBox>
      
      <OptionalSection>
        <p className="mb-4">Reflect:</p>
        <ul className="list-disc list-inside space-y-2 text-sm">
          <li>Think of a time an unexpected expense derailed you. How would a buffer have helped?</li>
          <li>What does financial security mean to you personally?</li>
        </ul>
        <WorksheetInput
          label="Your thoughts:"
          placeholder="Write anything that comes to mind..."
          value={data.reflection || ''}
          onChange={(v) => updateData('reflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
